import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    CircularProgress,
    Typography,
    Box
} from '@mui/material';

import { EventLocation } from '../data_types/eventTypes';

const ALL_OPTIONS = [
    { value: EventLocation.PENDING, label: 'Pending' },
    { value: EventLocation.PRIVATE, label: 'Private' },
    { value: EventLocation.INVITE, label: 'Invite only' },
    { value: EventLocation.PUBLIC, label: 'Public' },
    { value: EventLocation.ARCHIVED, label: 'Archived' }
];

interface MoveEventDialogProps {
    open: boolean;
    onClose: () => void;
    onMove: (targetLocation: EventLocation) => Promise<void>;
    eventName: string;
    currentType: EventLocation;
    loading: boolean;
    // Limits where the event can go (e.g. creators moving their own event); all other states when omitted
    allowedDestinations?: EventLocation[];
    // Optional note shown under the select for the chosen destination
    getDestinationNote?: (targetLocation: EventLocation) => string | undefined;
}

const MoveEventDialog: React.FC<MoveEventDialogProps> = ({
    open,
    onClose,
    onMove,
    eventName,
    currentType,
    loading,
    allowedDestinations,
    getDestinationNote
}) => {
    const availableOptions = ALL_OPTIONS.filter(option =>
        option.value !== currentType && (!allowedDestinations || allowedDestinations.includes(option.value)));
    const optionsKey = availableOptions.map(option => option.value).join(',');

    const [targetType, setTargetType] = useState<EventLocation>(availableOptions[0]?.value ?? EventLocation.PENDING);

    // Reset to the first available option when the dialog opens for a different event or state
    useEffect(() => {
        setTargetType(availableOptions[0]?.value ?? EventLocation.PENDING);
    }, [currentType, optionsKey, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTargetTypeChange = (event: SelectChangeEvent) => {
        setTargetType(event.target.value as EventLocation);
    };

    const handleMove = async () => {
        await onMove(targetType);
        onClose();
    };

    const getTypeLabel = (type: EventLocation) => {
        switch (type) {
            case EventLocation.PENDING: return 'Pending';
            case EventLocation.PRIVATE: return 'Private';
            case EventLocation.PUBLIC: return 'Public';
            case EventLocation.INVITE: return 'Invite only';
            case EventLocation.ARCHIVED: return 'Archived';
            case EventLocation.DRAFT: return 'Draft';
            default: return type;
        }
    };

    const defaultNote = targetType === EventLocation.INVITE
        ? 'Only invited players will see it. Players saved on the event are invited when it moves; add more under Invited players.'
        : currentType === EventLocation.INVITE
            ? 'Invited players lose access while it is out of Invite only. Their invites are kept if it moves back.'
            : undefined;
    const destinationNote = getDestinationNote?.(targetType) ?? defaultNote;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Move Event</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1">
                        <strong>Event:</strong> {eventName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Current Status:</strong> {getTypeLabel(currentType)}
                    </Typography>
                </Box>

                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Move to</InputLabel>
                    <Select
                        value={targetType}
                        onChange={handleTargetTypeChange}
                        label="Move to"
                        disabled={loading}
                    >
                        {availableOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {destinationNote && <FormHelperText>{destinationNote}</FormHelperText>}
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleMove}
                    variant="contained"
                    disabled={loading || availableOptions.length === 0}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? 'Moving...' : 'Move Event'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MoveEventDialog;
